import { useAuth } from './use-auth';

/**
 * Derived authentication status for routing decisions.
 *
 * This provides a single source of truth for auth state that drives
 * routing logic throughout the app.
 */
export type AuthStatus = 'loading' | 'loggedOut' | 'unverified' | 'verified';

/**
 * Hook that derives a single auth status from useAuth() to drive routing decisions.
 *
 * Status mapping:
 * - 'loading': Auth state is still being determined
 * - 'loggedOut': User is not authenticated
 * - 'unverified': User is logged in but email is not verified
 * - 'verified': User is logged in and email is verified
 *
 * @returns The current authentication status
 */
export function useAuthStatus(): AuthStatus {
  const { isLoading, isLoggedIn, user } = useAuth();

  // While auth state is loading, return loading status
  if (isLoading) {
    return 'loading';
  }

  // If not logged in, user is logged out
  if (!isLoggedIn || !user) {
    return 'loggedOut';
  }

  // If logged in but email is not verified, user is unverified
  // emailVerified must be explicitly true to be considered verified
  if (user.emailVerified !== true) {
    return 'unverified';
  }

  // User is logged in and email is verified
  return 'verified';
}
